import React, { useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormGenerator } from '@microservices/wiskey-react-components'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Tooltip,
  Typography,
} from '@mui/material'

import { PageContext } from '@pages/TimeLineCreateOrEdit/TimeLineCreateOrEdit'

import { CommandParameters } from '@components/CommandParameters'
import { JavaScriptField } from '@components/JavaScriptField'

import { BIND_TYPE, TYPES_SOURCE_OPTIONS } from '@constants'
import { AutocompleteOption } from '@types'

import { ModalRefPathPicker } from '@gantt/components/GanttCreateOrEdit/components/ModalRefPathPicker'

import { getColumnsObjectHeaderScriptHint } from '../../../../helpers'

export const ObjectXLinkObjectDataTab = () => {
  const { t } = useTranslation()
  const { state, inputs, handlers, data } = useContext(PageContext)
  const columnsObjectRef = useRef<HTMLDivElement>(null)
  const [objectXShowHeaderScriptDialog, setObjectXShowHeaderScriptDialog] = useState(false)

  const {
    watchObjectXObject,
    watchObjectXCommandCode,
    watchObjectYObject,
    watchObjectXColumnsObject,
    watchObjectXColumnsObjectCommandCode,
    watchObjectXColumnsObjectHeaderBindType,
    methods: { watch, setValue, trigger },
  } = state

  const { handleChangeActiveStep, handleChangeObjectYCurrentTab } = handlers

  const {
    objectXLinkFirstBlockInputs,
    objectXColumnsObjectInputs,
    objectXColumnsObjectHeaderInputs,
  } = inputs
  const { objectXSelectedColumnsObjectFields } = data

  const watchHeaderBindTypeId = watchObjectXColumnsObjectHeaderBindType?.id
  const isHeaderJsOrJsx =
    watchHeaderBindTypeId === BIND_TYPE.JS || watchHeaderBindTypeId === BIND_TYPE.JSX
  const isHeaderField = watchHeaderBindTypeId === BIND_TYPE.FIELD

  const handleGoToColumnsObject = () => {
    trigger('objectX.columnsObject.object', { shouldFocus: true })

    columnsObjectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleGoToObjectY = () => {
    handleChangeActiveStep(1)
    handleChangeObjectYCurrentTab(undefined, 0)
  }

  return (
    <>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='subtitle2'>
            {t('timeLinesCreateOrEdit.objectX.linkDataBlock.objectX')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormGenerator inputs={objectXLinkFirstBlockInputs} mainGridSpacing={0} />
          {!!watchObjectXObject?.id && (
            <Box sx={{ mt: 1 }}>
              {!!watchObjectXCommandCode && (
                <>
                  <CommandParameters
                    disableFieldType
                    commandCode={watchObjectXCommandCode.id}
                    containerSx={{ mb: 1 }}
                    label={t('contextMenuCreate.addMenuPointModal.parameters.label')}
                    labelWidth={120}
                    name='objectX.getDataCommand.parameters'
                    parametersMode='params'
                    sourceCode={watchObjectXObject.id as string}
                    sourceTitle={watchObjectXObject.label}
                    typeSource={TYPES_SOURCE_OPTIONS.OBJECT}
                  />
                  <CommandParameters
                    disableFieldType
                    commandCode={watchObjectXCommandCode.id}
                    containerSx={{ mb: 1 }}
                    label={t('contextMenuCreate.addMenuPointModal.body.label')}
                    labelWidth={120}
                    name='objectX.getDataCommand.body'
                    parametersMode='body'
                    sourceCode={watchObjectXObject.id as string}
                    sourceTitle={watchObjectXObject.label}
                    typeSource={TYPES_SOURCE_OPTIONS.OBJECT}
                  />
                </>
              )}
              <Box component='fieldset' mb={1}>
                <legend>{t('timeLinesCreateOrEdit.objectX.linkDataBlock.links')}</legend>
                <ModalRefPathPicker
                  hasClearInInput
                  refPickAllowed
                  embeddedObjectPickerControllerName='objectX.linkToObjectY.pathArray'
                  isDisabled={false}
                  label={t('timeLinesCreateOrEdit.objectX.linkDataBlock.toObjectY')}
                  name='objectX.linkToObjectY.keyField'
                  object={watchObjectXObject as AutocompleteOption<string>}
                  pickerName=''
                  labelSx={{
                    width: 230,
                  }}
                />
                <ModalRefPathPicker
                  hasClearInInput
                  refPickAllowed
                  embeddedObjectPickerControllerName='objectX.linkToColumnsObject.pathArray'
                  isDisabled={false}
                  label={t('timeLinesCreateOrEdit.objectX.linkDataBlock.toColumnsObject')}
                  name='objectX.linkToColumnsObject.keyField'
                  object={watchObjectXObject as AutocompleteOption<string>}
                  pickerName=''
                  labelSx={{
                    width: 230,
                  }}
                />
                {!watchObjectXColumnsObject?.id && (
                  <Alert
                    severity='info'
                    action={
                      <Button color='inherit' size='small' onClick={handleGoToColumnsObject}>
                        {t('timeLinesCreateOrEdit.objectX.linkDataBlock.goToColumnsObject')}
                      </Button>
                    }
                    sx={{
                      mt: 1,
                      mb: 1,
                      '& .MuiAlert-message': { alignSelf: 'center' },
                      '& .MuiAlert-icon': { alignSelf: 'center' },
                    }}
                  >
                    {t('timeLinesCreateOrEdit.objectX.linkDataBlock.columnsObjectNotSelected')}
                  </Alert>
                )}
                <ModalRefPathPicker
                  hasClearInInput
                  refPickAllowed
                  embeddedObjectPickerControllerName='objectX.fieldFromColumnsObjectToLink.pathArray'
                  isDisabled={!watchObjectXColumnsObject?.id}
                  label={t('timeLinesCreateOrEdit.objectX.linkDataBlock.fromColumnsObject')}
                  name='objectX.fieldFromColumnsObjectToLink.keyField'
                  object={(watchObjectXColumnsObject ?? {}) as AutocompleteOption<string>}
                  pickerName=''
                  labelSx={{
                    width: 230,
                  }}
                />
                {!watchObjectYObject?.id && (
                  <Alert
                    severity='info'
                    action={
                      <Button color='inherit' size='small' onClick={handleGoToObjectY}>
                        {t('timeLinesCreateOrEdit.objectX.linkDataBlock.goToObjectY')}
                      </Button>
                    }
                    sx={{
                      mt: 1,
                      mb: 1,
                      '& .MuiAlert-message': { alignSelf: 'center' },
                      '& .MuiAlert-icon': { alignSelf: 'center' },
                    }}
                  >
                    {t('timeLinesCreateOrEdit.objectX.linkDataBlock.objectYNotSelected')}
                  </Alert>
                )}
                <ModalRefPathPicker
                  hasClearInInput
                  refPickAllowed
                  embeddedObjectPickerControllerName='objectX.fieldFromObjectYToLink.pathArray'
                  isDisabled={!watchObjectYObject?.id}
                  label={t('timeLinesCreateOrEdit.objectX.linkDataBlock.fromObjectY')}
                  name='objectX.fieldFromObjectYToLink.keyField'
                  object={(watchObjectYObject ?? {}) as AutocompleteOption<string>}
                  pickerName=''
                  labelSx={{
                    width: 230,
                  }}
                />
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion ref={columnsObjectRef} defaultExpanded sx={{ mt: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant='subtitle2'>
              {t('timeLinesCreateOrEdit.objectX.linkDataBlock.columnsObject')}
            </Typography>
            <Tooltip
              placement='right'
              title={t('timeLinesCreateOrEdit.objectX.linkDataBlock.columnsObjectHint')}
            >
              <InfoOutlinedIcon color='action' fontSize='small' />
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormGenerator inputs={objectXColumnsObjectInputs} mainGridSpacing={0} />
          {!!watchObjectXColumnsObject?.id && !!watchObjectXColumnsObjectCommandCode && (
            <Box sx={{ mt: 1 }}>
              <CommandParameters
                disableFieldType
                commandCode={watchObjectXColumnsObjectCommandCode.id}
                containerSx={{ mb: 1 }}
                label={t('contextMenuCreate.addMenuPointModal.parameters.label')}
                labelWidth={120}
                name='objectX.columnsObject.command.parameters'
                parametersMode='params'
                sourceCode={watchObjectXColumnsObject.id as string}
                sourceTitle={watchObjectXColumnsObject.label}
                typeSource={TYPES_SOURCE_OPTIONS.OBJECT}
              />
              <CommandParameters
                disableFieldType
                commandCode={watchObjectXColumnsObjectCommandCode.id}
                containerSx={{ mb: 1 }}
                label={t('contextMenuCreate.addMenuPointModal.body.label')}
                labelWidth={120}
                name='objectX.columnsObject.command.body'
                parametersMode='body'
                sourceCode={watchObjectXColumnsObject.id as string}
                sourceTitle={watchObjectXColumnsObject.label}
                typeSource={TYPES_SOURCE_OPTIONS.OBJECT}
              />
            </Box>
          )}
          {!!watchObjectXColumnsObject?.id && (
            <Box component='fieldset' sx={{ mt: 1, mb: 1 }}>
              <legend>{t('timeLinesCreateOrEdit.objectX.linkDataBlock.headerBlockLegend')}</legend>
              <FormGenerator inputs={objectXColumnsObjectHeaderInputs} mainGridSpacing={0} />
              {isHeaderJsOrJsx && (
                <Box sx={{ mt: 1 }}>
                  <JavaScriptField
                    isShowScriptDialog={objectXShowHeaderScriptDialog}
                    scriptValue={watch('objectX.columnsObject.headerScript') ?? ''}
                    language={
                      watchHeaderBindTypeId === BIND_TYPE.JSX ? BIND_TYPE.JSX : BIND_TYPE.JS
                    }
                    onCloseScriptDialog={() => setObjectXShowHeaderScriptDialog(false)}
                    onOpenScriptDialog={() => setObjectXShowHeaderScriptDialog(true)}
                    onSaveScript={value =>
                      setValue('objectX.columnsObject.headerScript', value, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    {...getColumnsObjectHeaderScriptHint({
                      columnsObjectFields: objectXSelectedColumnsObjectFields,
                    })}
                  />
                </Box>
              )}
              {isHeaderField && (
                <Box sx={{ mt: 1 }}>
                  <ModalRefPathPicker
                    hasClearInInput
                    refPickAllowed
                    embeddedObjectPickerControllerName='objectX.columnsObject.headerField.pathArray'
                    isDisabled={false}
                    label={t('timeLinesCreateOrEdit.objectX.linkDataBlock.headerField')}
                    name='objectX.columnsObject.headerField.keyField'
                    object={(watchObjectXColumnsObject ?? {}) as AutocompleteOption<string>}
                    pickerName=''
                  />
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  )
}
